import { useState, useEffect, useCallback } from 'react';
import {
  getFloorSummaries,
  getTableSummariesForFloor,
  FloorSummary,
  TableSummary,
  toDateRangeParams
} from '../api/invoiceApi';
import { DateRange } from '../components/invoices/DateRangeSelector';

interface UseFloorSummaryOptions {
  /** Initial date range for fetching data */
  initialDateRange: DateRange;
  /** Whether to auto-fetch floors on mount */
  autoFetch?: boolean;
}

interface UseFloorSummaryReturn {
  /** List of floor summaries */
  floors: FloorSummary[];
  /** Tables indexed by floor ID */
  tablesByFloor: Record<number, TableSummary[]>;
  /** Selected floor ID (null = all floors) */
  selectedFloorId: number | null;
  /** Set of expanded floor IDs */
  expandedFloors: Set<number>;
  /** Whether floors are loading */
  loadingFloors: boolean;
  /** Loading state by floor ID */
  loadingTables: Record<number, boolean>;
  /** Error message if any */
  error: string | null;
  /** Current date range */
  dateRange: DateRange;
  /** Set the date range and refetch data */
  setDateRange: (range: DateRange) => void;
  /** Select a floor (null = all floors) */
  selectFloor: (floorId: number | null) => void;
  /** Toggle floor expansion */
  toggleFloorExpansion: (floorId: number) => void;
  /** Fetch tables for a specific floor */
  fetchTablesForFloor: (floorId: number) => Promise<void>;
  /** Refetch all floors */
  refetchFloors: () => Promise<void>;
  /** Get tables for selected floor or all tables */
  getSelectedFloorTables: () => TableSummary[];
  /** Clear cached table data */
  clearTableCache: () => void;
}

/**
 * Hook for managing floor and table summary data with date range filtering.
 * Handles fetching, caching, and state management for the Table Orders view.
 */
export function useFloorSummary(options: UseFloorSummaryOptions): UseFloorSummaryReturn {
  const { initialDateRange, autoFetch = true } = options;

  // Core data state
  const [floors, setFloors] = useState<FloorSummary[]>([]);
  const [tablesByFloor, setTablesByFloor] = useState<Record<number, TableSummary[]>>({});

  // Selection state
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [expandedFloors, setExpandedFloors] = useState<Set<number>>(new Set());

  // Date range state
  const [dateRange, setDateRangeState] = useState<DateRange>(initialDateRange);

  // Loading states
  const [loadingFloors, setLoadingFloors] = useState(false);
  const [loadingTables, setLoadingTables] = useState<Record<number, boolean>>({});

  // Error state
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch floor summaries from the API
   */
  const fetchFloors = useCallback(async () => {
    setLoadingFloors(true);
    setError(null);
    try {
      const params = toDateRangeParams(dateRange.startDate, dateRange.endDate);
      const data = await getFloorSummaries(params);
      setFloors(data);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch floors';
      setError(message);
      console.error('Error fetching floors:', err);
    } finally {
      setLoadingFloors(false);
    }
  }, [dateRange]);

  /**
   * Fetch tables for a specific floor
   */
  const fetchTablesForFloor = useCallback(async (floorId: number) => {
    // Skip if already loading
    if (loadingTables[floorId]) return;

    setLoadingTables(prev => ({ ...prev, [floorId]: true }));
    try {
      const params = toDateRangeParams(dateRange.startDate, dateRange.endDate);
      const data = await getTableSummariesForFloor(floorId, params);
      setTablesByFloor(prev => ({ ...prev, [floorId]: data }));
    } catch (err: any) {
      console.error(`Failed to fetch tables for floor ${floorId}:`, err);
      // Set empty array to indicate fetch was attempted
      setTablesByFloor(prev => ({ ...prev, [floorId]: [] }));
    } finally {
      setLoadingTables(prev => ({ ...prev, [floorId]: false }));
    }
  }, [dateRange, loadingTables]);

  /**
   * Toggle floor expansion and fetch tables if needed
   */
  const toggleFloorExpansion = useCallback((floorId: number) => {
    setExpandedFloors(prev => {
      const next = new Set(prev);
      if (next.has(floorId)) {
        next.delete(floorId);
      } else {
        next.add(floorId);
        // Fetch tables if not already loaded
        if (tablesByFloor[floorId] === undefined) {
          fetchTablesForFloor(floorId);
        }
      }
      return next;
    });
  }, [tablesByFloor, fetchTablesForFloor]);

  /**
   * Select a floor (null = all floors view)
   */
  const selectFloor = useCallback((floorId: number | null) => {
    setSelectedFloorId(floorId);
    // If selecting a specific floor, ensure its tables are loaded
    if (floorId !== null && tablesByFloor[floorId] === undefined) {
      fetchTablesForFloor(floorId);
    }
  }, [tablesByFloor, fetchTablesForFloor]);

  /**
   * Set date range and clear cached data
   */
  const setDateRange = useCallback((range: DateRange) => {
    setDateRangeState(range);
    // Clear cached tables when date range changes
    setTablesByFloor({});
    // Reset expanded floors to trigger re-fetch
    setExpandedFloors(new Set());
  }, []);

  /**
   * Clear the table cache (useful for refresh)
   */
  const clearTableCache = useCallback(() => {
    setTablesByFloor({});
    setExpandedFloors(new Set());
  }, []);

  /**
   * Get tables for the selected floor or all tables if none selected
   */
  const getSelectedFloorTables = useCallback((): TableSummary[] => {
    if (selectedFloorId === null) {
      // Return all tables from all floors
      return Object.values(tablesByFloor).flat();
    }
    return tablesByFloor[selectedFloorId] || [];
  }, [selectedFloorId, tablesByFloor]);

  // Fetch floors on mount and when date range changes
  useEffect(() => {
    if (autoFetch) {
      fetchFloors();
    }
  }, [fetchFloors, autoFetch]);

  return {
    floors,
    tablesByFloor,
    selectedFloorId,
    expandedFloors,
    loadingFloors,
    loadingTables,
    error,
    dateRange,
    setDateRange,
    selectFloor,
    toggleFloorExpansion,
    fetchTablesForFloor,
    refetchFloors: fetchFloors,
    getSelectedFloorTables,
    clearTableCache
  };
}

export default useFloorSummary;
